import React from "react";
import utils from "../../../utils/utils";

export const APPOINTMENT_PERIOD_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "TOMORROW", label: "Tomorrow" },
  { value: "WEEK", label: "This week" },
  { value: "Next Week", label: "Next week" },
  { value: "MONTH", label: "This month" },
  { value: "Custom", label: "Custom range" },
];

export const APPOINTMENT_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Confirmed", label: "Confirmed" },
  { value: "Arrived", label: "Arrived" },
  { value: "NotAttempt", label: "Did not attend" },
  { value: "Cancelled", label: "Cancelled" },
];

export const APPOINTMENT_STATUS_FILTER_OPTIONS = [
  { value: "Confirmed", label: "Confirmed" },
  { value: "Arrived", label: "Arrived" },
  { value: "NotAttempt", label: "Did not attend" },
  { value: "Cancelled", label: "Cancelled" },
];

/** Calendar default: Confirmed only; other statuses via multi-select chips. */
export const CALENDAR_DEFAULT_STATUS_IDS = ["Confirmed"];

/** Status colours for calendar identification (dot / border / badge). */
export const CALENDAR_APPOINTMENT_STATUS_META = {
  confirmed: {
    key: "confirmed",
    value: "Confirmed",
    label: "Confirmed",
    shortLabel: "C",
    color: "#059669",
    background: "#d1fae5",
  },
  arrived: {
    key: "arrived",
    value: "Arrived",
    label: "Arrived",
    shortLabel: "A",
    color: "#2563eb",
    background: "#dbeafe",
  },
  notattempt: {
    key: "notattempt",
    value: "NotAttempt",
    label: "Did not attend",
    shortLabel: "DNA",
    color: "#d97706",
    background: "#fef3c7",
  },
  cancelled: {
    key: "cancelled",
    value: "Cancelled",
    label: "Cancelled",
    shortLabel: "X",
    color: "#dc2626",
    background: "#fee2e2",
  },
};

export function getCalendarAppointmentStatusMeta(status) {
  const key = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  const normalized =
    key === "notattempt" ||
    key.includes("didnotattend") ||
    key.includes("didnot")
      ? "notattempt"
      : key;
  return (
    CALENDAR_APPOINTMENT_STATUS_META[normalized] || {
      key: "default",
      value: status || "",
      label: normalizeAppointmentStatusLabel(status),
      shortLabel: "?",
      color: "#64748b",
      background: "#f1f5f9",
    }
  );
}

export const CALENDAR_STATUS_LEGEND_ITEMS = APPOINTMENT_STATUS_FILTER_OPTIONS.map(
  (option) => {
    const meta = getCalendarAppointmentStatusMeta(option.value);
    return {
      value: option.value,
      label: option.label,
      shortLabel: meta.shortLabel,
      color: meta.color,
      background: meta.background,
    };
  },
);

export const APPOINTMENT_CHANGE_TYPES = {
  CANCELLED: "CancelledAppList",
  OUT_OF_WINDOW: "OutOfWindowAppList",
  ADHOC: "AdhocvisitAppList",
  DID_NOT_ATTEND: "DidnotattendAppList",
  RESCHEDULED: "ResheduledAppList",
};

/** Legacy Appointment Changes dropdown — same ActionType values as GetAppointmentReasons. */
export const APPOINTMENT_CHANGE_TYPE_OPTIONS = [
  { value: APPOINTMENT_CHANGE_TYPES.ADHOC, label: "Adhoc appointments", shortLabel: "Adhoc" },
  {
    value: APPOINTMENT_CHANGE_TYPES.CANCELLED,
    label: "Cancelled appointments",
    shortLabel: "Cancelled",
  },
  {
    value: APPOINTMENT_CHANGE_TYPES.DID_NOT_ATTEND,
    label: "Did not attend appointments",
    shortLabel: "Did not attend",
  },
  {
    value: APPOINTMENT_CHANGE_TYPES.OUT_OF_WINDOW,
    label: "Out of window appointments",
    shortLabel: "Out of window",
  },
  {
    value: APPOINTMENT_CHANGE_TYPES.RESCHEDULED,
    label: "Rescheduled appointments",
    shortLabel: "Rescheduled",
  },
];

export function appointmentChangeTypeLabel(actionType) {
  const match = APPOINTMENT_CHANGE_TYPE_OPTIONS.find((opt) => opt.value === actionType);
  return match?.shortLabel || match?.label || String(actionType || "—");
}

export const APPOINTMENT_LIST_FIELD_OPTIONS = [
  { key: "Participant", label: "Participant" },
  { key: "StudyGroupVisit", label: "Study · group · visit" },
  { key: "StartTime", label: "Date & time" },
  { key: "AppointmentStatus", label: "Status" },
  { key: "Staff", label: "Staff" },
  { key: "ScreeningRandomId", label: "Screening & random ID" },
  { key: "LocationName", label: "Location" },
  { key: "BookedBy", label: "Booked by" },
  { key: "BookedOn", label: "Booked date" },
  { key: "AppointmentType", label: "Type" },
];

export const APPOINTMENT_LIST_DEFAULT_COLUMNS = [
  "Participant",
  "StudyGroupVisit",
  "StartTime",
  "AppointmentStatus",
  "Staff",
  "ScreeningRandomId",
  "LocationName",
];

export const APPOINTMENT_LIST_SORTABLE_FIELDS = [
  "Participant",
  "StudyGroupVisit",
  "StartTime",
  "BookedBy",
  "BookedOn",
];

/** Parts for the combined Study / Group / Visit column. */
export function getAppointmentStudyGroupVisitParts(row = {}) {
  const study = String(
    getAppointmentRowValue(row, "StudyName") || row?.StudyName || "",
  ).trim();
  const group = String(
    getAppointmentRowValue(row, "GroupName") || row?.GroupName || "",
  ).trim();
  const visit = String(
    getAppointmentRowValue(row, "VisitName") || row?.VisitName || "",
  ).trim();
  return {
    study: study || "—",
    group: group || "—",
    visit: visit || "—",
    text: [study, group, visit].filter(Boolean).join(" · ") || "—",
  };
}

/**
 * Schedule visit-type key for badges (screening / residential / non_residential / adhoc).
 * Matches schedule VisitTypeBadge colors — not StudySchColor.
 */
export function getAppointmentVisitTypeKey(row = {}) {
  const visitId = String(
    getAppointmentRowValue(row, "VisitIdRef") || row?.VisitIdRef || "",
  )
    .trim()
    .toUpperCase();
  const visitName = String(
    getAppointmentRowValue(row, "VisitName") || row?.VisitName || "",
  ).trim();
  const visitType = String(
    getAppointmentRowValue(row, "VisitType") || row?.VisitType || "",
  )
    .trim()
    .toLowerCase();
  const visitNo = Number(row?.Visit ?? row?.visit);
  const adhocFlag = String(row?.AdhocVisit ?? row?.adhocVisit ?? "").trim();

  if (
    visitId === "ADHOCVISIT" ||
    adhocFlag === "1" ||
    /adhoc/i.test(visitName) ||
    visitType.includes("adhoc")
  ) {
    return "adhoc";
  }
  if (
    visitNo === 0 ||
    visitType.includes("screening") ||
    /screening/i.test(visitName)
  ) {
    return "screening";
  }
  if (visitType.includes("non")) return "non_residential";
  if (visitType === "residential" || visitType.startsWith("resid")) {
    return "residential";
  }
  return "non_residential";
}

export function getAppointmentVisitTypeBadgeClass(row = {}) {
  switch (getAppointmentVisitTypeKey(row)) {
    case "screening":
      return "is-screening";
    case "residential":
      return "is-res";
    case "adhoc":
      return "is-adhoc";
    default:
      return "is-non-res";
  }
}

const APPOINTMENT_ROW_FIELD_ALIASES = {
  FirstName: ["FirstName", "firstName", "ForeName", "foreName"],
  LastName: ["LastName", "lastName", "SurName", "surName"],
  VolName: ["VolName", "volName"],
  StudyName: ["StudyName", "studyName"],
  StudyNumber: ["StudyNumber", "studyNumber", "StudyNo", "studyNo"],
  StudyIdRef: ["StudyIdRef", "studyIdRef", "StudyId", "studyId"],
  StudySchColor: [
    "StudySchColor",
    "studySchColor",
    "StudyColor",
    "studyColor",
    "color",
    "Color",
  ],
  GroupName: ["GroupName", "groupName"],
  GroupId: ["GroupId", "groupId", "GroupNameId", "groupNameId", "GroupNameIdRef"],
  VisitName: ["VisitName", "visitName"],
  VisitType: ["VisitType", "visitType"],
  VisitIdRef: [
    "VisitIdRef",
    "visitIdRef",
    "VisitId",
    "visitId",
    "VisitsPerStudyId",
    "visitsPerStudyId",
  ],
  LocationId: ["LocationId", "locationId"],
  RandomId: ["RandomId", "randomId", "RdId", "rdId", "ScreeningId", "screeningId"],
  // Calendar StudyWiseView uses `start` / `end` (FullCalendar shape).
  StartTime: ["StartTime", "startTime", "start", "Start"],
  EndTime: ["EndTime", "endTime", "end", "End"],
  SlotDuration: ["SlotDuration", "slotDuration"],
  AppointmentStatus: ["AppointmentStatus", "appointmentStatus"],
  LocationName: [
    "LocationName",
    "locationName",
    "SiteName",
    "siteName",
    "Location",
    "location",
  ],
  AppointmentType: ["AppointmentType", "appointmentType"],
  BookedBy: ["BookedBy", "bookedBy", "CreatedByName", "createdByName"],
  UpdatedBy: [
    "UpdatedByName",
    "updatedByName",
    "CreatedByName",
    "createdByName",
    "BookedBy",
    "bookedBy",
    "ModifiedByName",
    "modifiedByName",
    "UpdatedBy",
    "updatedBy",
    "ModifiedBy",
    "modifiedBy",
  ],
  Reason: [
    "Reason",
    "reason",
    "ReasonForBookApp",
    "reasonForBookApp",
    "ReasonForOutOfWindow",
    "reasonForOutOfWindow",
    "AdhocReason",
    "adhocReason",
    "CancellationReason",
    "cancellationReason",
    "CancelledReason",
    "cancelledReason",
    "NotAttemptReason",
    "notAttemptReason",
  ],
  BookedOn: [
    "BookedOn",
    "bookedOn",
    "CreatedDate",
    "createdDate",
    "SystemDateTime",
    "SystemDatetime",
    "systemDateTime",
  ],
  StaffNames: ["StaffNames", "staffNames"],
};

/** Assigned staff names from list API (pipe / semicolon / comma separated). */
export function parseAppointmentStaffNames(row = {}) {
  const raw = String(
    getAppointmentRowValue(row, "StaffNames") ??
      row?.StaffNames ??
      row?.staffNames ??
      "",
  ).trim();
  if (!raw) return [];
  return raw
    .split(/[|;,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Read appointment list row values with PascalCase / camelCase / legacy aliases. */
export function getAppointmentRowValue(row, field) {
  if (!row || typeof row !== "object") return null;
  const aliases = APPOINTMENT_ROW_FIELD_ALIASES[field] || [field];
  for (const key of aliases) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  for (const key of aliases) {
    const match = Object.keys(row).find(
      (candidate) => candidate.toLowerCase() === String(key || "").toLowerCase(),
    );
    if (
      match &&
      row[match] !== undefined &&
      row[match] !== null &&
      String(row[match]).trim() !== ""
    ) {
      return row[match];
    }
  }
  return null;
}

export function matchAppointmentColumnSearch(row, field, value) {
  const needle = String(value || "").trim().toLowerCase();
  if (!needle) return true;
  if (field === "Participant") {
    const haystack = [
      row?.VolName,
      row?.volName,
      row?.FirstName,
      row?.LastName,
      row?.VolId,
      row?.VolunteerNo,
      row?.VolunteerId,
    ]
      .map((part) => String(part ?? "").toLowerCase())
      .join(" ");
    return haystack.includes(needle);
  }
  if (field === "ScreeningRandomId") {
    const haystack = [
      row?.ScreeningId,
      row?.screeningId,
      row?.RdId,
      row?.rdId,
      row?.RandomisedId,
      row?.randomisedId,
      row?.RandomId,
      row?.randomId,
    ]
      .map((part) => String(part ?? "").toLowerCase())
      .join(" ");
    return haystack.includes(needle);
  }
  if (field === "AppointmentStatus") {
    const raw = String(getAppointmentRowValue(row, field) ?? "").toLowerCase();
    const normalized = /notattempt|did not attend/i.test(raw)
      ? "notattempt"
      : raw;
    return (
      normalized.includes(needle) ||
      raw.includes(needle) ||
      (needle === "notattempt" && /did not attend|notattempt/i.test(raw))
    );
  }
  if (field === "Staff") {
    const names = parseAppointmentStaffNames(row).map((name) =>
      name.toLowerCase(),
    );
    if (!names.length) return false;
    return names.some(
      (name) => name === needle || name.includes(needle),
    );
  }
  const haystack = String(getAppointmentRowValue(row, field) ?? "").toLowerCase();
  return haystack.includes(needle);
}

/** Sort key for appointment ReportTable columns. */
export function getAppointmentSortValue(row, field) {
  if (field === "Participant") {
    return [
      row?.VolName,
      row?.volName,
      [row?.FirstName, row?.LastName].filter(Boolean).join(" "),
      row?.VolId,
      row?.VolunteerNo,
    ]
      .map((part) => String(part ?? "").trim())
      .find(Boolean) || "";
  }
  if (field === "StudyGroupVisit") {
    return getAppointmentStudyGroupVisitParts(row).text.toLowerCase();
  }
  if (field === "BookedBy") {
    return String(getAppointmentRowValue(row, "BookedBy") ?? "").trim();
  }
  if (field === "BookedOn") {
    const raw = String(
      getAppointmentRowValue(row, "BookedOn") || row?.BookedOn || "",
    ).trim();
    return parseAppointmentCreatedAt(raw)?.getTime() ?? 0;
  }
  if (field === "Staff") {
    return parseAppointmentStaffNames(row).join(" ").toLowerCase();
  }
  if (field === "ScreeningRandomId") {
    return [
      row?.ScreeningId,
      row?.screeningId,
      row?.RdId,
      row?.RandomisedId,
      row?.RandomId,
    ]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" ");
  }
  if (field === "StartTime") {
    const raw = String(
      getAppointmentRowValue(row, "StartTime") || row?.StartTime || "",
    ).trim();
    return parseAppointmentCreatedAt(raw)?.getTime() ?? 0;
  }
  return String(getAppointmentRowValue(row, field) ?? "");
}

export function formatAppointmentSlotDuration(value) {
  const text = String(value ?? "").trim();
  if (!text) return "—";
  if (/minute/i.test(text)) return text;
  return `${text} minutes`;
}

export const APPOINTMENT_CHANGE_FIELD_OPTIONS = [
  { key: "ChangeType", label: "Type" },
  { key: "Participant", label: "Participant" },
  { key: "StudyGroupVisit", label: "Study · group · visit" },
  { key: "AppDateTime", label: "Appointment date & time" },
  { key: "SystemDateTime", label: "Last updated" },
  { key: "Reason", label: "Reason" },
  { key: "UpdatedBy", label: "Updated by" },
];

export const APPOINTMENT_CHANGE_DEFAULT_COLUMNS = [
  "ChangeType",
  "Participant",
  "StudyGroupVisit",
  "AppDateTime",
  "SystemDateTime",
  "Reason",
];

export const VOLUNTEER_SEARCH_FIELD_OPTIONS = [
  { key: "VolName", label: "Name" },
  { key: "VolId", label: "Participant ID" },
  { key: "MobileNo", label: "Mobile" },
  { key: "EmailId", label: "Email" },
  { key: "DOB", label: "Date of birth" },
  { key: "StudyName", label: "Study" },
];

export const VOLUNTEER_SEARCH_DEFAULT_COLUMNS = [
  "VolName",
  "VolId",
  "MobileNo",
  "EmailId",
  "StudyName",
];

export const OCCUPANCY_FIELD_OPTIONS = [
  { key: "LocationName", label: "Location" },
  { key: "RoomName", label: "Room" },
  { key: "OccupiedSlots", label: "Occupied" },
  { key: "AvailableSlots", label: "Available" },
  { key: "TotalSlots", label: "Total slots" },
  { key: "OccupancyPercent", label: "Occupancy %" },
];

export const OCCUPANCY_DEFAULT_COLUMNS = [
  "LocationName",
  "RoomName",
  "OccupiedSlots",
  "AvailableSlots",
  "TotalSlots",
  "OccupancyPercent",
];

const STATUS_PILL_STYLES = {
  pending: { background: "#e0e7ff", color: "#3730a3" },
  confirmed: { background: "#d1fae5", color: "#065f46" },
  arrived: { background: "#dbeafe", color: "#1e40af" },
  cancelled: { background: "#fee2e2", color: "#991b1b" },
  notattempt: { background: "#fef3c7", color: "#92400e" },
  rescheduled: { background: "#ede9fe", color: "#5b21b6" },
  default: { background: "#f3f4f6", color: "#374151" },
};

export function normalizeAppointmentStatusLabel(status) {
  const text = String(status || "").trim();
  if (!text) return "—";
  if (text.toLowerCase() === "notattempt") return "Did not attend";
  return text;
}

/** Status-linked reason for Cancelled / DNA (empty when none). */
export function getAppointmentStatusReason(row) {
  if (!row) return "";
  const status = String(
    getAppointmentRowValue(row, "AppointmentStatus") ??
      row?.AppointmentStatus ??
      "",
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  const pick = (...keys) => {
    for (const key of keys) {
      const value = String(
        getAppointmentRowValue(row, key) ?? row?.[key] ?? "",
      ).trim();
      if (value) return value;
    }
    return "";
  };
  if (status === "cancelled") {
    return pick("CancelledReason", "CancellationReason", "Reason", "ReasonForBookApp");
  }
  if (
    status === "notattempt" ||
    status.includes("didnotattend") ||
    status.includes("didnot")
  ) {
    return pick("NotAttemptReason", "Reason", "ReasonForBookApp");
  }
  return pick("Reason", "ReasonForOutOfWindow", "ReasonForBookApp");
}

function parseAppointmentStartDate(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  const legacy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (legacy) {
    return new Date(
      Number(legacy[3]),
      Number(legacy[2]) - 1,
      Number(legacy[1]),
    );
  }
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Client-side date-range match for table column filters (All Time / preset / custom). */
export function matchesAppointmentDateRangeFilter(value, dateRange) {
  const preset = String(dateRange?.preset || dateRange?.dateRange || "All Time")
    .trim();
  if (!preset || preset === "All Time") return true;
  const startIso = String(dateRange?.startDate || dateRange?.dateFrom || "").trim();
  const endIso = String(dateRange?.endDate || dateRange?.dateTo || "").trim();
  if (!startIso && !endIso) return true;
  const date = parseAppointmentStartDate(value);
  if (!date) return false;
  const day = startOfLocalDay(date).getTime();
  if (startIso) {
    const start = parseAppointmentStartDate(startIso);
    if (start && day < startOfLocalDay(start).getTime()) return false;
  }
  if (endIso) {
    const end = parseAppointmentStartDate(endIso);
    if (end && day > startOfLocalDay(end).getTime()) return false;
  }
  return true;
}

function startOfLocalDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * AQ legacy status rules (ViewAppointments / IndividualAppointments):
 * - No Pending status
 * - Arrived only when appointment date is today or past (DisabledArrived)
 * - Once Arrived, status locked except Principal Investigator / System Administrator
 * - Withdrawn volunteer (VolStatus=1) cannot change status
 * - InActive appointment: Confirmed / DNA options disabled
 * - VisitWiseCount=2 + DNA: status locked
 * - Cancel only when CheckCancel=Cancel and not Arrived / not inactive
 */
export function getAppointmentStatusChangeState(appointment = {}, userData = {}) {
  const current = String(
    getAppointmentRowValue(appointment, "AppointmentStatus") ||
      appointment?.AppointmentStatus ||
      "",
  ).trim();
  const currentKey = current.toLowerCase() === "notattempt" ? "NotAttempt" : current;
  const volStatus = String(
    getAppointmentRowValue(appointment, "VolStatus") ??
      appointment?.VolStatus ??
      "",
  ).trim();
  const inActive = String(
    getAppointmentRowValue(appointment, "InActive") ??
      appointment?.InActive ??
      "0",
  ).trim();
  const visitWiseCount = Number(
    getAppointmentRowValue(appointment, "VisitWiseCount") ??
      appointment?.VisitWiseCount ??
      0,
  );
  const disabledArrivedFlag = String(
    getAppointmentRowValue(appointment, "DisabledArrived") ??
      appointment?.DisabledArrived ??
      "",
  )
    .trim()
    .toLowerCase();
  const startRaw =
    getAppointmentRowValue(appointment, "StartTime") ||
    appointment?.StartTime ||
    "";
  const startDate = parseAppointmentStartDate(startRaw);
  const isFutureAppointment =
    disabledArrivedFlag === "disabled" ||
    (startDate != null && startDate > startOfLocalDay());

  const role = String(userData?.Role || userData?.Roles || "")
    .trim()
    .toLowerCase();
  const canOverrideArrived =
    role === "principal investigator" ||
    role === "system administrator" ||
    role === "super admin" ||
    userData?.IsPlatformSuperAdmin === true;

  const isArrived = currentKey === "Arrived";
  const isCancelled = currentKey.toLowerCase() === "cancelled";
  const isWithdrawn = volStatus === "1";
  const isInactive = inActive === "1";
  const dnaLocked =
    visitWiseCount === 2 && currentKey === "NotAttempt";

  let controlDisabled = false;
  let controlReason = "";
  if (isWithdrawn) {
    controlDisabled = true;
    controlReason = "Status cannot be changed for a withdrawn participant.";
  } else if (dnaLocked) {
    controlDisabled = true;
    controlReason =
      "Status cannot be changed after did not attend for this visit.";
  } else if (isCancelled) {
    controlDisabled = true;
    controlReason = "Cancelled appointments cannot change status here.";
  } else if (isArrived && !canOverrideArrived) {
    controlDisabled = true;
    controlReason =
      "Arrived status can only be changed by a Principal Investigator or System Administrator.";
  }

  const canCancelAction =
    !controlDisabled &&
    !isArrived &&
    !isInactive &&
    (String(appointment?.CheckCancel ?? "") === "Cancel" ||
      /confirmed|notattempt/i.test(currentKey));

  const options = [
    {
      value: "Confirmed",
      label: "Confirmed",
      disabled: isInactive && currentKey !== "Confirmed",
      reason: isInactive
        ? "Confirmed is not available for an inactive appointment."
        : "",
    },
    {
      value: "Arrived",
      label: "Arrived",
      disabled: isFutureAppointment && currentKey !== "Arrived",
      reason: isFutureAppointment
        ? "Arrived cannot be selected for future appointment(s)."
        : "",
    },
    {
      value: "NotAttempt",
      label: "Did not attend",
      disabled: isInactive && currentKey !== "NotAttempt",
      reason: isInactive
        ? "Did not attend is not available for an inactive appointment."
        : "",
    },
    {
      value: "Cancelled",
      label: "Cancelled",
      disabled: !canCancelAction && currentKey.toLowerCase() !== "cancelled",
      reason: !canCancelAction
        ? "Cancel is not available for this appointment."
        : "",
    },
  ].map((option) => {
    if (option.value === currentKey) {
      return { ...option, disabled: false, reason: "" };
    }
    return option;
  });

  return {
    currentStatus: currentKey || "Confirmed",
    controlDisabled,
    controlReason,
    canOverrideArrived,
    isFutureAppointment,
    options,
  };
}

export function getAppointmentStatusPillStyle(status) {
  const key = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  return STATUS_PILL_STYLES[key] || STATUS_PILL_STYLES.default;
}

export function renderAppointmentStatusPill(status) {
  const label = normalizeAppointmentStatusLabel(status);
  const style = getAppointmentStatusPillStyle(status);
  const shown =
    label && label.length > 20 ? `${label.slice(0, 20)}...` : label;
  return (
    <span
      className="eplanner-status-pill"
      title={label || undefined}
      style={{
        display: "inline-block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: "0.04rem 0.4rem",
        borderRadius: "999px",
        fontFamily: '"Manrope", "Segoe UI", system-ui, sans-serif',
        fontSize: "0.66rem",
        fontWeight: 600,
        lineHeight: 1.25,
        ...style,
      }}
    >
      {shown}
    </span>
  );
}

export function getAppointmentFieldLabel(field) {
  if (field === "actions") return "Actions";
  if (field === "ScreeningRandomId") return "Screening & random ID";
  const option = APPOINTMENT_LIST_FIELD_OPTIONS.find((row) => row.key === field);
  return option?.label || field;
}

export function getAppointmentRowId(row) {
  return String(
    row?.AppointmentId ??
      row?.appointmentId ??
      row?.VolIdRef ??
      row?.volIdRef ??
      `${row?.VolName || row?.FirstName || "row"}-${row?.StartTime || row?.AppDateTime || ""}`,
  );
}

const EPLANNER_GUID_RE =
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/** True when value is a uniqueidentifier (hyphens optional). */
export function isEplannerGuid(value) {
  return EPLANNER_GUID_RE.test(String(value || "").trim());
}

/**
 * Volunteer GUID for ePlanner SPs (VolIdRef / VolunteerDemRecordId).
 * Fast list exposes numeric VolunteerId ("189") separately — never send that.
 */
export function resolveVolunteerGuid(row = {}) {
  const candidates = [
    row?.VolIdRef,
    row?.volIdRef,
    row?.VolunteerDemRecordId,
    row?.volunteerDemRecordId,
    row?.VolunteerId,
    row?.volunteerId,
    row?.VolId,
    row?.volId,
  ];
  for (const candidate of candidates) {
    const text = String(candidate ?? "").trim();
    if (text && isEplannerGuid(text)) return text;
  }
  return "";
}

/** Parse creation datetime (incl. time) for latest-created ranking. */
function parseAppointmentCreatedAt(value) {
  const text = String(value || "").trim();
  if (!text) return null;

  const legacy = text.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i,
  );
  if (legacy) {
    let hour = legacy[4] != null ? Number(legacy[4]) : 0;
    const minute = legacy[5] != null ? Number(legacy[5]) : 0;
    const second = legacy[6] != null ? Number(legacy[6]) : 0;
    const ampm = (legacy[7] || "").toUpperCase();
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    const date = new Date(
      Number(legacy[3]),
      Number(legacy[2]) - 1,
      Number(legacy[1]),
      hour,
      minute,
      second,
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (iso) {
    const date = new Date(
      Number(iso[1]),
      Number(iso[2]) - 1,
      Number(iso[3]),
      iso[4] != null ? Number(iso[4]) : 0,
      iso[5] != null ? Number(iso[5]) : 0,
      iso[6] != null ? Number(iso[6]) : 0,
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Creation-time score for "current" appointment.
 * Prefer SystemDateTime / CreatedDate. VisitWiseCount=1 means newest created
 * (SP: ROW_NUMBER … ORDER BY SystemDatetime DESC). Do not use StartTime/SortNo.
 */
function appointmentCreationScore(row) {
  const created = String(
    getAppointmentRowValue(row, "SystemDateTime") ??
      row?.SystemDateTime ??
      row?.SystemDatetime ??
      getAppointmentRowValue(row, "CreatedDate") ??
      row?.CreatedDate ??
      "",
  ).trim();
  const createdTs = parseAppointmentCreatedAt(created)?.getTime();
  if (createdTs) return createdTs;

  // Lower VisitWiseCount = newer creation (1 = current).
  const visitWise = Number(
    getAppointmentRowValue(row, "VisitWiseCount") ?? row?.VisitWiseCount,
  );
  if (Number.isFinite(visitWise) && visitWise > 0) {
    return Number.MAX_SAFE_INTEGER - visitWise;
  }
  return 0;
}

/** All-day calendar label — defaults (background + text). */
export const CALENDAR_LABEL_COLOR = "#8a8378";
export const CALENDAR_LABEL_BG = "#f3efe6";
export const CALENDAR_LABEL_TEXT = "#3f3a36";
export const CALENDAR_LABEL_BORDER = "#8a8378";

function normalizeHexColor(value, fallback) {
  const raw = String(value || "").trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
    if (raw.length === 4) {
      return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
    }
    return raw.toLowerCase();
  }
  return fallback;
}

/** Label background colour (`Color` column). */
export function resolveCalendarLabelBackground(rowOrColor) {
  if (typeof rowOrColor === "string") {
    return normalizeHexColor(rowOrColor, CALENDAR_LABEL_BG);
  }
  const row = rowOrColor || {};
  return normalizeHexColor(
    row.Color ?? row.color ?? row.StudySchColor ?? row.studySchColor,
    CALENDAR_LABEL_BG,
  );
}

/** Label text / foreground colour (`TextColor` column). */
export function resolveCalendarLabelTextColor(row) {
  return normalizeHexColor(
    row?.TextColor ?? row?.textColor ?? row?.ForeColor ?? row?.foreColor,
    CALENDAR_LABEL_TEXT,
  );
}

/** Accent / border — same as background when custom bg is set. */
export function resolveCalendarLabelColor(row) {
  return resolveCalendarLabelBackground(row);
}

export const CALENDAR_LABEL_COLOR_OPTIONS = [
  "#f3efe6",
  "#DC7633",
  "#1f6f5b",
  "#2c5282",
  "#9b2c2c",
  "#6b46c1",
  "#b7791f",
  "#2d3748",
];

export function isAllDayAppointment(row) {
  if (!row || typeof row !== "object") return false;
  const value = row.allDay ?? row.AllDay ?? row.IsAllDay ?? row.isAllDay;
  if (value === true || value === 1) return true;
  const text = String(value ?? "")
    .trim()
    .toLowerCase();
  return text === "1" || text === "true";
}

/** Dedicated label row or legacy AllDay appointment used as a named calendar label. */
export function isCalendarLabel(row) {
  if (!row || typeof row !== "object") return false;
  const flag = row.IsCalendarLabel ?? row.isCalendarLabel;
  if (flag === true || flag === 1) return true;
  const text = String(flag ?? "")
    .trim()
    .toLowerCase();
  if (text === "1" || text === "true") return true;
  if (String(row.CalendarLabelId ?? row.calendarLabelId ?? "").trim()) {
    return true;
  }
  return isAllDayAppointment(row);
}

/**
 * Label comment text from list/detail rows.
 * Skips blank strings so an empty LabelComment does not hide Comment.
 */
export function resolveCalendarLabelComment(row) {
  if (!row || typeof row !== "object") return "";
  const candidates = [
    row.LabelComment,
    row.labelComment,
    row.Comment,
    row.comment,
    row.Notes,
    row.notes,
  ];
  for (const value of candidates) {
    if (value == null) continue;
    // Merge rows expose AppNotes as INT — never treat numbers as comment text.
    if (typeof value === "number") continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function appointmentVisitKey(row) {
  if (isCalendarLabel(row)) {
    const id = String(
      row?.CalendarLabelId ??
        row?.calendarLabelId ??
        row?.AppointmentId ??
        row?.appointmentId ??
        "",
    )
      .trim()
      .toUpperCase();
    return id ? `label|${id}` : "";
  }
  if (isAllDayAppointment(row)) {
    const id = String(row?.AppointmentId ?? row?.appointmentId ?? "")
      .trim()
      .toUpperCase();
    return id ? `allday|${id}` : "";
  }
  const vol = String(
    getAppointmentRowValue(row, "VolIdRef") ?? row?.VolIdRef ?? "",
  )
    .trim()
    .toUpperCase();
  const study = String(
    getAppointmentRowValue(row, "StudyIdRef") ?? row?.StudyIdRef ?? "",
  )
    .trim()
    .toUpperCase();
  const visit = String(
    getAppointmentRowValue(row, "VisitIdRef") ?? row?.VisitIdRef ?? "ADHOC",
  )
    .trim()
    .toUpperCase();
  const group = String(
    getAppointmentRowValue(row, "GroupId") ??
      row?.GroupId ??
      getAppointmentRowValue(row, "GroupNameIdRef") ??
      row?.GroupNameIdRef ??
      "",
  )
    .trim()
    .toUpperCase();
  return `${vol}|${study}|${visit}|${group}`;
}

/**
 * Current = latest SystemDateTime per volunteer + study + visit + group.
 * Same AppointmentId collapsed first; older bookings are duplicates.
 * Caller should pass Cancelled rows too when ranking, then drop Cancelled
 * if the latest status is Cancelled (otherwise older Confirmed resurfaces).
 */
export function uniqueAppointmentRows(rows = [], options = {}) {
  const {
    currentVisitOnly = true,
    dropCancelledLatest = false,
  } = options;
  if (!Array.isArray(rows) || !rows.length) return [];

  const byAppointmentId = new Map();
  let noIdIndex = 0;
  for (const row of rows) {
    const id = String(row?.AppointmentId ?? row?.appointmentId ?? "")
      .trim()
      .toUpperCase();
    const key = id || `__noid_${noIdIndex++}`;
    const prev = byAppointmentId.get(key);
    if (!prev || appointmentCreationScore(row) >= appointmentCreationScore(prev)) {
      byAppointmentId.set(key, row);
    }
  }
  const uniqueById = [...byAppointmentId.values()];
  if (!currentVisitOnly) return uniqueById;

  const byVisit = new Map();
  for (const row of uniqueById) {
    const key = appointmentVisitKey(row);
    const prev = byVisit.get(key);
    if (!prev || appointmentCreationScore(row) >= appointmentCreationScore(prev)) {
      byVisit.set(key, row);
    }
  }
  let current = [...byVisit.values()];
  if (dropCancelledLatest) {
    current = current.filter((row) => {
      const status = String(
        getAppointmentRowValue(row, "AppointmentStatus") ??
          row?.AppointmentStatus ??
          "",
      ).toLowerCase();
      return status !== "cancelled";
    });
  }
  return current;
}

/**
 * List order: booking datetime (StartTime / BookedDate) latest first.
 * Tie-break by creation time so freshly booked rows stay on top.
 */
export function sortAppointmentsByBookingDateLatestFirst(rows = []) {
  if (!Array.isArray(rows) || rows.length < 2) return Array.isArray(rows) ? [...rows] : [];
  return [...rows].sort((a, b) => {
    const aRaw = String(
      getAppointmentRowValue(a, "StartTime") || a?.StartTime || "",
    ).trim();
    const bRaw = String(
      getAppointmentRowValue(b, "StartTime") || b?.StartTime || "",
    ).trim();
    const aTs = parseAppointmentCreatedAt(aRaw)?.getTime() ?? 0;
    const bTs = parseAppointmentCreatedAt(bRaw)?.getTime() ?? 0;
    if (bTs !== aTs) return bTs - aTs;
    return appointmentCreationScore(b) - appointmentCreationScore(a);
  });
}

/**
 * History table: Date & time (StartTime) newest first (descending).
 * Tie-break by creation / booked-on time.
 */
export function sortAppointmentHistoryRowsDescending(rows = []) {
  if (!Array.isArray(rows) || rows.length < 2) {
    return Array.isArray(rows) ? [...rows] : [];
  }
  return [...rows].sort((a, b) => {
    const aStart = appointmentBookingTimestamp(a);
    const bStart = appointmentBookingTimestamp(b);
    if (bStart !== aStart) return bStart - aStart;
    return appointmentCreationScore(b) - appointmentCreationScore(a);
  });
}

/**
 * History table: older bookings for the same visit (replaced / rescheduled)
 * are superseded — UI strikethroughs the whole row. Only the latest per visit
 * stays normal (lowest VisitWiseCount when present, else highest creation score).
 */
export function buildAppointmentHistorySupersededKeySet(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const superseded = new Set();
  if (list.length < 2) return superseded;

  const byVisit = new Map();
  list.forEach((row, index) => {
    const visitKey = appointmentVisitKey(row) || `__row_${index}`;
    if (!byVisit.has(visitKey)) byVisit.set(visitKey, []);
    byVisit.get(visitKey).push({ row, index });
  });

  const visitWiseOf = (row) => {
    const n = Number(
      getAppointmentRowValue(row, "VisitWiseCount") ?? row?.VisitWiseCount,
    );
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  for (const group of byVisit.values()) {
    if (group.length < 2) continue;

    let best = group[0];
    for (let i = 1; i < group.length; i += 1) {
      const item = group[i];
      const aWise = visitWiseOf(item.row);
      const bWise = visitWiseOf(best.row);
      if (aWise != null && bWise != null) {
        if (aWise < bWise) {
          best = item;
          continue;
        }
        if (aWise > bWise) continue;
      }
      if (appointmentCreationScore(item.row) > appointmentCreationScore(best.row)) {
        best = item;
      }
    }

    const bestKey = appointmentHistoryRowKey(best.row, best.index);
    group.forEach((item) => {
      const key = appointmentHistoryRowKey(item.row, item.index);
      if (key !== bestKey) superseded.add(key);
    });
  }
  return superseded;
}

export function appointmentHistoryRowKey(row, index = 0) {
  const id = String(row?.AppointmentId ?? row?.appointmentId ?? "")
    .trim()
    .toUpperCase();
  if (id) return id;
  const visitKey = appointmentVisitKey(row) || "x";
  const score = appointmentCreationScore(row);
  return `row-${visitKey}-${score}-${index}`;
}

function appointmentBookingTimestamp(row) {
  const raw = String(
    getAppointmentRowValue(row, "StartTime") || row?.StartTime || "",
  ).trim();
  return parseAppointmentCreatedAt(raw)?.getTime() ?? 0;
}

function compareText(a, b) {
  return String(a || "")
    .trim()
    .localeCompare(String(b || "").trim(), undefined, { sensitivity: "base" });
}

/**
 * Sort appointment list rows.
 * @param {"latest"|"oldest"|"name"|"study"|"status"|"visit"} sortBy
 */
export function sortAppointmentsList(rows = [], sortBy = "latest") {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (list.length < 2) return list;
  switch (sortBy) {
    case "oldest":
      return list.sort((a, b) => {
        const diff = appointmentBookingTimestamp(a) - appointmentBookingTimestamp(b);
        if (diff !== 0) return diff;
        return appointmentCreationScore(a) - appointmentCreationScore(b);
      });
    case "name":
      return list.sort((a, b) => {
        const aName = [
          getAppointmentRowValue(a, "VolName"),
          getAppointmentRowValue(a, "FirstName"),
          getAppointmentRowValue(a, "LastName"),
        ]
          .filter(Boolean)
          .join(" ");
        const bName = [
          getAppointmentRowValue(b, "VolName"),
          getAppointmentRowValue(b, "FirstName"),
          getAppointmentRowValue(b, "LastName"),
        ]
          .filter(Boolean)
          .join(" ");
        return compareText(aName, bName);
      });
    case "study":
      return list.sort((a, b) =>
        compareText(
          getAppointmentRowValue(a, "StudyName"),
          getAppointmentRowValue(b, "StudyName"),
        ),
      );
    case "status":
      return list.sort((a, b) =>
        compareText(
          getAppointmentRowValue(a, "AppointmentStatus"),
          getAppointmentRowValue(b, "AppointmentStatus"),
        ),
      );
    case "visit":
      return list.sort((a, b) =>
        compareText(
          getAppointmentRowValue(a, "VisitName"),
          getAppointmentRowValue(b, "VisitName"),
        ),
      );
    case "latest":
    default:
      return sortAppointmentsByBookingDateLatestFirst(list);
  }
}

function bookingDayKey(row) {
  const raw = String(
    getAppointmentRowValue(row, "StartTime") || row?.StartTime || "",
  ).trim();
  const date = parseAppointmentCreatedAt(raw);
  if (!date) return { key: "unknown", label: "No date", sort: 0 };
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const dayNum = date.getDate();
  const d = String(dayNum).padStart(2, "0");
  const key = `${y}-${m}-${d}`;
  const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
  const today = startOfLocalDay(new Date());
  const dayStart = startOfLocalDay(date);
  const diffDays = Math.round((dayStart - today) / 86400000);
  let label = `${weekday}, ${d}/${m}/${y}`;
  if (diffDays === 0) label = `Today · ${d}/${m}/${y}`;
  else if (diffDays === 1) label = `Tomorrow · ${d}/${m}/${y}`;
  else if (diffDays === -1) label = `Yesterday · ${d}/${m}/${y}`;
  return { key, label, sort: dayStart.getTime() };
}

function appointmentGroupBucket(row, groupBy) {
  if (groupBy === "date") {
    const day = bookingDayKey(row);
    return {
      key: `date:${day.key}`,
      label: day.label,
      sort: -day.sort,
      kind: "Date",
    };
  }

  if (groupBy === "study") {
    const studyId = String(getAppointmentRowValue(row, "StudyIdRef") || "").trim();
    const studyNo = String(
      getAppointmentRowValue(row, "StudyNumber") || "",
    ).trim();
    const studyName = String(
      getAppointmentRowValue(row, "StudyName") || "No study",
    ).trim();
    const label = [studyNo, studyName].filter(Boolean).join(" · ") || "No study";
    return {
      key: `study:${(studyId || studyName).toLowerCase()}`,
      label,
      sort: studyName.toLowerCase(),
      kind: "Study",
    };
  }

  if (groupBy === "status") {
    const label = normalizeAppointmentStatusLabel(
      getAppointmentRowValue(row, "AppointmentStatus"),
    );
    return {
      key: `status:${label.toLowerCase()}`,
      label,
      sort: label.toLowerCase(),
      kind: "Status",
    };
  }

  if (groupBy === "visit") {
    const visitId = String(
      getAppointmentRowValue(row, "VisitIdRef") || row?.VisitIdRef || "",
    ).trim();
    const visitName = String(
      getAppointmentRowValue(row, "VisitName") || "No visit",
    ).trim();
    return {
      key: `visit:${(visitId || visitName).toLowerCase()}`,
      label: visitName,
      sort: visitName.toLowerCase(),
      kind: "Visit",
    };
  }

  if (groupBy === "group") {
    const groupId = String(
      getAppointmentRowValue(row, "GroupId") || row?.GroupId || "",
    ).trim();
    const groupName = String(
      getAppointmentRowValue(row, "GroupName") || "No group",
    ).trim();
    return {
      key: `group:${(groupId || groupName).toLowerCase()}`,
      label: groupName,
      sort: groupName.toLowerCase(),
      kind: "Group",
    };
  }

  return null;
}

/**
 * Group sorted appointment rows for list UI.
 * @param {"none"|"date"|"study"|"status"|"visit"|"group"} groupBy
 * @returns {null | Array<{ key: string, label: string, kind?: string, rows: object[] }>}
 */
export function groupAppointmentsList(rows = [], groupBy = "none") {
  if (!Array.isArray(rows) || !rows.length || groupBy === "none") return null;

  const buckets = new Map();
  rows.forEach((row) => {
    const meta = appointmentGroupBucket(row, groupBy);
    if (!meta) return;
    if (!buckets.has(meta.key)) {
      buckets.set(meta.key, {
        key: meta.key,
        label: meta.label,
        kind: meta.kind,
        sort: meta.sort,
        rows: [],
      });
    }
    buckets.get(meta.key).rows.push(row);
  });

  return [...buckets.values()].sort((a, b) => {
    if (typeof a.sort === "number" && typeof b.sort === "number") {
      return a.sort - b.sort;
    }
    return compareText(a.label, b.label);
  });
}

export function formatAppointmentDateTime(value) {
  return utils.formatDateTimeDisplay(value);
}

function trimWindowDate(value) {
  const text = String(value ?? "").trim();
  return text && text !== "—" && text !== "-" ? text : "";
}

/** Parse legacy DD/MM/YYYY (or ISO) for sort; returns timestamp or null. */
export function parseWindowDateSortValue(value) {
  const text = trimWindowDate(value);
  if (!text) return null;
  const legacy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (legacy) {
    const day = Number(legacy[1]);
    const month = Number(legacy[2]);
    const year = Number(legacy[3]);
    const ts = Date.UTC(year, month - 1, day);
    return Number.isNaN(ts) ? null : ts;
  }
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const ts = Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(ts) ? null : ts;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

/**
 * Normalize a GetWindowAppointments / TentativeDates row for the preview list.
 * @returns {{ visitName: string, dueLabel: string, windowLabel: string, pendingRefLabel: string, sortKey: number }}
 */
export function formatWindowAppointmentRow(row = {}) {
  const visitName = String(row?.VisitName ?? row?.visitName ?? "Visit").trim() || "Visit";
  const start = trimWindowDate(row?.TentativeStartDate ?? row?.tentativeStartDate);
  const due = trimWindowDate(row?.TentativeDefaultDate ?? row?.tentativeDefaultDate);
  const end = trimWindowDate(row?.TentativeEndDate ?? row?.tentativeEndDate);
  const refVisit = String(row?.RefVisitName ?? row?.refVisitName ?? "").trim();

  const dueLabel = due ? `Due ${due}` : "";
  let windowLabel = "";
  if (start && end) windowLabel = `Window ${start} – ${end}`;
  else if (start) windowLabel = `Window from ${start}`;
  else if (end) windowLabel = `Window until ${end}`;

  const hasDates = Boolean(due || start || end);
  const pendingRefLabel = hasDates
    ? ""
    : `Dates after ${refVisit || "reference visit"} is booked`;

  const sortKey =
    parseWindowDateSortValue(due) ??
    parseWindowDateSortValue(start) ??
    parseWindowDateSortValue(end) ??
    Number.POSITIVE_INFINITY;

  return {
    visitName,
    dueLabel,
    windowLabel,
    pendingRefLabel,
    sortKey,
    /** Primary line when due is missing but range exists */
    primaryDateLabel: dueLabel || windowLabel,
  };
}

export function sortWindowAppointmentRows(rows = []) {
  return [...(Array.isArray(rows) ? rows : [])]
    .map((row, index) => ({ row, index, meta: formatWindowAppointmentRow(row) }))
    .sort((a, b) => {
      if (a.meta.sortKey !== b.meta.sortKey) return a.meta.sortKey - b.meta.sortKey;
      return a.index - b.index;
    })
    .map((item) => item.row);
}

function visitIdKey(row) {
  return String(
    row?.VisitsPerStudyId ??
      row?.visitsPerStudyId ??
      row?.VisitIdRef ??
      row?.visitIdRef ??
      row?.VisitId ??
      "",
  )
    .trim()
    .toUpperCase();
}

/**
 * AQ GetWindowAppointments omits Visit 0 (Screening) and Visit 1.
 * Merge those unbooked visits so the preview can offer Book for Visit 1, etc.
 */
export function mergeBookableWindowRows({
  windowRows = [],
  studyVisits = [],
  bookedRows = [],
  groupId = "",
} = {}) {
  const bookedIds = new Set(
    (Array.isArray(bookedRows) ? bookedRows : [])
      .filter((row) => {
        const status = String(
          getAppointmentRowValue(row, "AppointmentStatus") ||
            row?.AppointmentStatus ||
            "",
        ).toLowerCase();
        return status === "confirmed" || status === "arrived";
      })
      .map(visitIdKey)
      .filter(Boolean),
  );

  // Prefer the appointment group when present; still allow Visit 0/1 from the
  // study when group differs (Screening vs dosing group).
  const groupKey = String(groupId || "").trim().toUpperCase();
  const earlyRows = (Array.isArray(studyVisits) ? studyVisits : [])
    .filter((visit) => {
      const visitNo = Number(
        visit?.Visit ?? visit?.visit ?? visit?.VisitNo ?? visit?.visitNo,
      );
      if (!(visitNo === 0 || visitNo === 1)) return false;
      const id = visitIdKey(visit);
      return Boolean(id) && !bookedIds.has(id);
    })
    .sort((a, b) => {
      const aGroup = String(
        a?.GroupNameIdRef ?? a?.groupNameIdRef ?? a?.GroupId ?? "",
      )
        .trim()
        .toUpperCase();
      const bGroup = String(
        b?.GroupNameIdRef ?? b?.groupNameIdRef ?? b?.GroupId ?? "",
      )
        .trim()
        .toUpperCase();
      const aMatch = groupKey && aGroup === groupKey ? 0 : 1;
      const bMatch = groupKey && bGroup === groupKey ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (
        Number(a?.Visit ?? a?.visit ?? 0) - Number(b?.Visit ?? b?.visit ?? 0)
      );
    })
    .map((visit) => ({
      VisitsPerStudyId: visitIdKey(visit),
      VisitName: String(visit?.VisitName ?? visit?.visitName ?? "Visit").trim(),
      Visit: Number(visit?.Visit ?? visit?.visit ?? visit?.VisitNo ?? 0),
      GroupNameIdRef:
        visit?.GroupNameIdRef ?? visit?.groupNameIdRef ?? groupId ?? "",
      _earlyBookable: true,
    }));

  // One Screening + one Visit 1 max (prefer group match from sort above).
  const earlyUnique = [];
  const earlyVisitNos = new Set();
  for (const row of earlyRows) {
    const visitNo = Number(row.Visit);
    if (earlyVisitNos.has(visitNo)) continue;
    earlyVisitNos.add(visitNo);
    earlyUnique.push(row);
  }

  const seen = new Set(earlyUnique.map((row) => visitIdKey(row)));
  const rest = (Array.isArray(windowRows) ? windowRows : []).filter((row) => {
    const id = visitIdKey(row);
    if (!id) return true;
    if (bookedIds.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  return sortWindowAppointmentRows([...earlyUnique, ...rest]);
}

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isResidentialVisitType(row = {}) {
  const raw = String(row?.VisitType ?? row?.visitType ?? "").trim().toLowerCase();
  if (!raw) return false;
  if (raw.includes("non")) return false;
  return raw === "residential" || raw.startsWith("resid");
}

function formatUtcAsDdMmYyyy(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

function shiftIsoDate(isoYmd, days) {
  const text = String(isoYmd || "").trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const base = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(base.getTime())) return "";
  base.setUTCDate(base.getUTCDate() + toFiniteNumber(days, 0));
  const y = base.getUTCFullYear();
  const m = String(base.getUTCMonth() + 1).padStart(2, "0");
  const d = String(base.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDaysToIsoDate(isoYmd, days) {
  const shifted = shiftIsoDate(isoYmd, days);
  if (!shifted) return "";
  const [y, m, d] = shifted.split("-");
  return `${d}/${m}/${y}`;
}

function hasWindowDates(row = {}) {
  return Boolean(
    trimWindowDate(row?.TentativeStartDate ?? row?.tentativeStartDate) ||
      trimWindowDate(row?.TentativeDefaultDate ?? row?.tentativeDefaultDate) ||
      trimWindowDate(row?.TentativeEndDate ?? row?.tentativeEndDate),
  );
}

function normalizeVisitWindowMeta(row = {}) {
  const id = visitIdKey(row);
  const visitNo = Number(
    row?.Visit ?? row?.visit ?? row?.VisitNo ?? row?.visitNo,
  );
  const visitTypeRaw = String(row?.VisitType ?? row?.visitType ?? "").trim();
  const isScreening =
    visitNo === 0 ||
    /screening/i.test(visitTypeRaw) ||
    /screening/i.test(String(row?.VisitName ?? row?.visitName ?? ""));
  return {
    id,
    visitNo: Number.isFinite(visitNo) ? visitNo : null,
    visitName: String(row?.VisitName ?? row?.visitName ?? "Visit").trim() || "Visit",
    groupId: String(
      row?.GroupNameIdRef ?? row?.groupNameIdRef ?? row?.GroupId ?? "",
    )
      .trim()
      .toUpperCase(),
    refVisitId: String(row?.RefVisitId ?? row?.refVisitId ?? "")
      .trim()
      .toUpperCase(),
    refVisitName: String(
      row?.RefVisitWindow ?? row?.refVisitWindow ?? row?.RefVisitName ?? "",
    ).trim(),
    isScreening,
    isResidential: isResidentialVisitType(row),
    startPeriod: toFiniteNumber(row?.StartPeriod ?? row?.startPeriod, 0),
    finishPeriod: toFiniteNumber(row?.FinishPeriod ?? row?.finishPeriod, 0),
    periodDuration: toFiniteNumber(
      row?.PeriodDuration ?? row?.periodDuration,
      NaN,
    ),
    tolerencePeriod: toFiniteNumber(
      row?.TolerencePeriod ?? row?.tolerencePeriod,
      0,
    ),
    upperTolerenceLimit: toFiniteNumber(
      row?.UpperTolerenceLimit ?? row?.upperTolerenceLimit,
      0,
    ),
    nonResidVisitOccur: toFiniteNumber(
      row?.NonResidVisitOccur ?? row?.nonResidVisitOccur,
      0,
    ),
    nonResidTolerencePeriod: toFiniteNumber(
      row?.NonResidTolerencePeriod ?? row?.nonResidTolerencePeriod,
      0,
    ),
    nonResidUpperTolerenceLimit: toFiniteNumber(
      row?.NonResidUpperTolerenceLimit ?? row?.nonResidUpperTolerenceLimit,
      NaN,
    ),
    screeningWindowStart: toFiniteNumber(
      row?.ScreeningWindowStart ?? row?.screeningWindowStart,
      NaN,
    ),
    screeningWindowFinish: toFiniteNumber(
      row?.ScreeningWindowFinish ?? row?.screeningWindowFinish,
      NaN,
    ),
    screeningDuration: toFiniteNumber(
      row?.ScreeningDuration ?? row?.screeningDuration,
      NaN,
    ),
  };
}

/**
 * Protocol due day (RefVisit + Day offset). Stored in dateMap for ref chaining.
 * Same day-offset rules as Schedule AI
 * (`scheduleAppointmentTimeline.projectVisitAppointmentDate`):
 * - Screening → anchor + screeningWindowStart
 * - Other visits → RefVisit date + startPeriod / nonResidVisitOccur
 */
function projectScheduleStyleDueIso(visit, dateMap, allVisits, anchorIso) {
  if (!visit?.id || !anchorIso) return "";
  if (dateMap.has(visit.id)) return dateMap.get(visit.id);

  if (visit.isScreening || visit.visitNo === 0) {
    const offset = Number.isFinite(visit.screeningWindowStart)
      ? visit.screeningWindowStart
      : 0;
    const iso = shiftIsoDate(anchorIso, offset) || anchorIso;
    dateMap.set(visit.id, iso);
    return iso;
  }

  let baseIso = anchorIso;
  if (visit.refVisitId) {
    const refVisit = allVisits.find((row) => row.id === visit.refVisitId);
    if (refVisit) {
      baseIso =
        projectScheduleStyleDueIso(refVisit, dateMap, allVisits, anchorIso) ||
        anchorIso;
    }
  }

  const dayOffset = visit.isResidential
    ? visit.startPeriod
    : visit.nonResidVisitOccur;
  const iso = shiftIsoDate(baseIso, dayOffset) || baseIso;
  dateMap.set(visit.id, iso);
  return iso;
}

/** Min (−) / Max (+) day allowances from schedule visit setup. */
function resolveVisitWindowTolerances(visit) {
  if (!visit || visit.isScreening || visit.visitNo === 0) {
    return { minus: 0, plus: 0 };
  }
  const minusRaw = visit.isResidential
    ? visit.tolerencePeriod
    : visit.nonResidTolerencePeriod || visit.tolerencePeriod;
  const plusRaw = visit.isResidential
    ? visit.upperTolerenceLimit
    : Number.isFinite(visit.nonResidUpperTolerenceLimit)
      ? visit.nonResidUpperTolerenceLimit
      : visit.upperTolerenceLimit;
  return {
    minus: Number.isFinite(minusRaw) ? Math.abs(minusRaw) : 0,
    plus: Number.isFinite(plusRaw) ? Math.abs(plusRaw) : 0,
  };
}

/**
 * Display window around the protocol due day:
 * Expected start = due − Min, Expected end = due + Max.
 * Screening uses screeningWindowStart / screeningWindowFinish from anchor.
 */
function projectScheduleStyleWindowBounds(
  visit,
  dueIso,
  allVisits,
  anchorIso,
  dateMap,
) {
  if (!visit?.id) {
    return { startIso: dueIso || "", endIso: dueIso || "" };
  }

  if (visit.isScreening || visit.visitNo === 0) {
    const startIso = dueIso || "";
    let endIso = startIso;
    if (Number.isFinite(visit.screeningWindowFinish) && anchorIso) {
      endIso =
        shiftIsoDate(anchorIso, visit.screeningWindowFinish) || startIso;
    }
    return { startIso, endIso };
  }

  if (!dueIso) {
    return { startIso: "", endIso: "" };
  }

  const { minus, plus } = resolveVisitWindowTolerances(visit);
  const startIso = minus
    ? shiftIsoDate(dueIso, -minus) || dueIso
    : dueIso;

  // Residential end is finishPeriod + Max from the same RefVisit base (not due+Max).
  if (visit.isResidential) {
    let baseIso = anchorIso;
    if (visit.refVisitId) {
      const refVisit = allVisits.find((row) => row.id === visit.refVisitId);
      if (refVisit) {
        baseIso =
          projectScheduleStyleDueIso(refVisit, dateMap, allVisits, anchorIso) ||
          anchorIso;
      }
    }
    const endOffset =
      (Number.isFinite(visit.finishPeriod) ? visit.finishPeriod : 0) + plus;
    const endIso = shiftIsoDate(baseIso, endOffset) || dueIso;
    return { startIso, endIso };
  }

  const endIso = plus ? shiftIsoDate(dueIso, plus) || dueIso : dueIso;
  return { startIso, endIso };
}

function scheduleDayOffsetLabel(visit) {
  if (!visit) return null;
  if (visit.isScreening || visit.visitNo === 0) {
    const offset = Number.isFinite(visit.screeningWindowStart)
      ? visit.screeningWindowStart
      : 0;
    return offset;
  }
  return visit.isResidential ? visit.startPeriod : visit.nonResidVisitOccur;
}

/** Protocol window allowance shown as ±N or -N/+M. */
function schedulePlusMinusLabel(visit) {
  if (!visit || visit.isScreening || visit.visitNo === 0) return "";
  const { minus: minusSafe, plus: plusSafe } = resolveVisitWindowTolerances(visit);
  if (!minusSafe && !plusSafe) return "";
  if (minusSafe === plusSafe) return `±${minusSafe}`;
  return `−${minusSafe}/+${plusSafe}`;
}

function makeTimelineRow(
  visit,
  {
    startIso,
    endIso,
    dueIso = null,
    booked = false,
    current = false,
    dayOffset = null,
  },
) {
  const startDisplay = startIso ? addDaysToIsoDate(startIso, 0) : "";
  const endDisplay = endIso ? addDaysToIsoDate(endIso, 0) : startDisplay;
  const dueDisplay = dueIso ? addDaysToIsoDate(dueIso, 0) : startDisplay;
  const plusMinus = schedulePlusMinusLabel(visit);
  return {
    VisitsPerStudyId: visit.id,
    VisitName: visit.visitName,
    Visit: visit.visitNo,
    GroupNameIdRef: visit.groupId,
    RefVisitName: visit.refVisitName,
    RefVisitId: visit.refVisitId,
    TentativeStartDate: startDisplay,
    TentativeDefaultDate: dueDisplay,
    TentativeEndDate: endDisplay,
    DayOffset: dayOffset,
    PlusMinusLabel: plusMinus,
    _projected: !booked,
    _booked: booked,
    _currentBooking: current,
    _clickable: !booked,
    // Preferred book date = protocol due day (centre of Min/Max window).
    _expectedStartIso: dueIso || startIso || "",
  };
}

/** Normalize ISO / DD/MM/YYYY / DD-MM-YYYY to yyyy-MM-dd for date pickers. */
export function toBookingDateIso(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const legacy = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (legacy) {
    const day = legacy[1].padStart(2, "0");
    const month = legacy[2].padStart(2, "0");
    const year = legacy[3];
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isoFromAppointmentStart(value) {
  return toBookingDateIso(value);
}

/** Preferred book date from a window / tentative visit row (protocol due day). */
export function getWindowVisitExpectedDateIso(visitRow = {}) {
  return (
    toBookingDateIso(visitRow?._expectedStartIso) ||
    toBookingDateIso(
      visitRow?.TentativeDefaultDate ?? visitRow?.tentativeDefaultDate,
    ) ||
    toBookingDateIso(
      visitRow?.TentativeStartDate ?? visitRow?.tentativeStartDate,
    ) ||
    toBookingDateIso(
      visitRow?.TentativeEndDate ?? visitRow?.tentativeEndDate,
    ) ||
    ""
  );
}

function collectTimelineVisits(visitDetailRows = [], plannerVisits = [], groupId = "") {
  const groupKey = String(groupId || "")
    .trim()
    .toUpperCase();
  const byId = new Map();

  const ingest = (row) => {
    const visit = normalizeVisitWindowMeta(row);
    if (!visit.id) return;
    const prev = byId.get(visit.id);
    if (!prev) {
      byId.set(visit.id, visit);
      return;
    }
    byId.set(visit.id, {
      ...visit,
      ...prev,
      visitName: prev.visitName || visit.visitName,
      visitNo: Number.isFinite(prev.visitNo) ? prev.visitNo : visit.visitNo,
      refVisitId: prev.refVisitId || visit.refVisitId,
      startPeriod: prev.startPeriod || visit.startPeriod,
      finishPeriod: prev.finishPeriod || visit.finishPeriod,
      periodDuration: Number.isFinite(prev.periodDuration)
        ? prev.periodDuration
        : visit.periodDuration,
      tolerencePeriod: prev.tolerencePeriod || visit.tolerencePeriod,
      upperTolerenceLimit: prev.upperTolerenceLimit || visit.upperTolerenceLimit,
      nonResidVisitOccur: prev.nonResidVisitOccur || visit.nonResidVisitOccur,
      nonResidTolerencePeriod:
        prev.nonResidTolerencePeriod || visit.nonResidTolerencePeriod,
      nonResidUpperTolerenceLimit: Number.isFinite(prev.nonResidUpperTolerenceLimit)
        ? prev.nonResidUpperTolerenceLimit
        : visit.nonResidUpperTolerenceLimit,
      screeningWindowStart: Number.isFinite(prev.screeningWindowStart)
        ? prev.screeningWindowStart
        : visit.screeningWindowStart,
      screeningWindowFinish: Number.isFinite(prev.screeningWindowFinish)
        ? prev.screeningWindowFinish
        : visit.screeningWindowFinish,
      screeningDuration: Number.isFinite(prev.screeningDuration)
        ? prev.screeningDuration
        : visit.screeningDuration,
      isResidential: prev.isResidential || visit.isResidential,
      isScreening: prev.isScreening || visit.isScreening,
    });
  };

  (Array.isArray(visitDetailRows) ? visitDetailRows : []).forEach(ingest);
  (Array.isArray(plannerVisits) ? plannerVisits : []).forEach(ingest);

  return [...byId.values()]
    .filter((visit) => {
      if (!groupKey) return true;
      if (visit.visitNo === 0 || visit.isScreening) return true;
      return !visit.groupId || visit.groupId === groupKey;
    })
    .sort((a, b) => {
      const an = Number.isFinite(a.visitNo) ? a.visitNo : 9999;
      const bn = Number.isFinite(b.visitNo) ? b.visitNo : 9999;
      return an - bn;
    });
}

function buildBookedVisitDateMap(bookedAppointments = []) {
  const map = new Map();
  for (const appt of Array.isArray(bookedAppointments) ? bookedAppointments : []) {
    const status = String(
      getAppointmentRowValue(appt, "AppointmentStatus") ||
        appt?.AppointmentStatus ||
        "",
    )
      .trim()
      .toLowerCase();
    if (status !== "confirmed" && status !== "arrived") continue;

    const visitId = visitIdKey({
      VisitsPerStudyId:
        getAppointmentRowValue(appt, "VisitIdRef") ||
        appt?.VisitIdRef ||
        appt?.VisitsPerStudyId,
    });
    if (!visitId) continue;

    const iso = isoFromAppointmentStart(
      getAppointmentRowValue(appt, "StartTime") || appt?.StartTime,
    );
    if (!iso) continue;

    const display = addDaysToIsoDate(iso, 0);
    const prev = map.get(visitId);
    if (!prev || iso < prev.iso) {
      map.set(visitId, { iso, display, status });
    }
  }
  return map;
}

/**
 * Full smart timeline — aligned with Schedule AI
 * (`scheduleAppointmentTimeline.projectVisitAppointmentDate`):
 * Appointment date = first-booking anchor; each visit = RefVisit + Day offset.
 * Booked visits keep actual dates (non-clickable).
 */
export function buildSmartTentativeWindowRows({
  visitDetailRows = [],
  plannerVisits = [],
  bookedAppointments = [],
  groupId = "",
  anchorDateIso = "",
  selectedVisitId = "",
} = {}) {
  const anchorIso = String(anchorDateIso || "").trim().slice(0, 10);
  const hasAnchor = /^\d{4}-\d{2}-\d{2}$/.test(anchorIso);
  const selectedKey = String(selectedVisitId || "")
    .trim()
    .toUpperCase();
  if (selectedKey === "ADHOCVISIT") return [];
  // Window section waits for study + group + date (caller also gates this).
  if (!hasAnchor || !String(groupId || "").trim()) return [];

  const timeline = collectTimelineVisits(
    visitDetailRows,
    plannerVisits,
    groupId,
  );
  if (!timeline.length) return [];

  const bookedMap = buildBookedVisitDateMap(bookedAppointments);
  const selected =
    (selectedKey && timeline.find((visit) => visit.id === selectedKey)) || null;

  // Seed booked visits so later RefVisit projections use real dates.
  const dateMap = new Map();
  for (const [id, info] of bookedMap.entries()) {
    dateMap.set(id, info.iso);
  }

  const rows = [];
  for (const visit of timeline) {
    const booked = bookedMap.get(visit.id);
    const dayOffset = scheduleDayOffsetLabel(visit);
    const isSelected = Boolean(selected && visit.id === selected.id);

    if (booked) {
      const bounds = projectScheduleStyleWindowBounds(
        visit,
        booked.iso,
        timeline,
        anchorIso,
        dateMap,
      );
      const row = makeTimelineRow(visit, {
        startIso: booked.iso,
        endIso: bounds.endIso || booked.iso,
        dueIso: booked.iso,
        booked: true,
        dayOffset,
      });
      row.AppointmentStatus = booked.status;
      rows.push(row);
      continue;
    }

    const dueIso = projectScheduleStyleDueIso(
      visit,
      dateMap,
      timeline,
      anchorIso,
    );
    if (!dueIso) continue;

    const bounds = projectScheduleStyleWindowBounds(
      visit,
      dueIso,
      timeline,
      anchorIso,
      dateMap,
    );

    rows.push(
      makeTimelineRow(visit, {
        startIso: bounds.startIso || dueIso,
        endIso: bounds.endIso || dueIso,
        dueIso,
        current: isSelected,
        dayOffset,
      }),
    );
  }

  return rows;
}

/**
 * Timeline rows win (full Screening → Visit N story). Merge any leftover API
 * window rows that are not already on the timeline.
 */
export function mergeApiAndSmartWindowRows({
  apiRows = [],
  smartRows = [],
} = {}) {
  const byId = new Map();

  for (const row of Array.isArray(smartRows) ? smartRows : []) {
    const id = visitIdKey(row);
    if (!id) continue;
    byId.set(id, row);
  }

  for (const row of Array.isArray(apiRows) ? apiRows : []) {
    const id = visitIdKey(row);
    if (!id) continue;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, { ...row, _clickable: hasWindowDates(row) });
      continue;
    }
    if (existing._booked) continue;
    if (!hasWindowDates(existing) && hasWindowDates(row)) {
      byId.set(id, {
        ...existing,
        TentativeStartDate:
          row.TentativeStartDate ?? row.tentativeStartDate ?? "",
        TentativeDefaultDate:
          row.TentativeDefaultDate ?? row.tentativeDefaultDate ?? "",
        TentativeEndDate: row.TentativeEndDate ?? row.tentativeEndDate ?? "",
        RefVisitName:
          existing.RefVisitName ||
          existing.refVisitName ||
          row.RefVisitName ||
          "",
        _projected: true,
        _clickable: existing._clickable !== false,
      });
    }
  }

  return [...byId.values()].sort((a, b) => {
    const an = Number(a?.Visit ?? a?.visit);
    const bn = Number(b?.Visit ?? b?.visit);
    const aSafe = Number.isFinite(an) ? an : 9999;
    const bSafe = Number.isFinite(bn) ? bn : 9999;
    if (aSafe !== bSafe) return aSafe - bSafe;
    return 0;
  });
}

/** Planner StudyList historically prefixed Employee unique-code (EMP) by SP bug. */
function normalizePlannerStudyNumber(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  // Strip mistaken Employee prefix until DB SP is redeployed.
  return text.replace(/^(EMP)\s+/i, "").trim();
}

function normalizePlannerStudyColor(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (text.startsWith("#") || text.startsWith("rgb")) return text;
  if (/^[0-9a-fA-F]{6}$/.test(text)) return `#${text}`;
  if (/^[0-9a-fA-F]{3}$/.test(text)) return `#${text}`;
  return text;
}

export function mapStudyOptions(rows = []) {
  return rows.map((row) => ({
    value: String(
      row?.StudyScheduleId ??
        row?.studyScheduleId ??
        row?.StudyIdRef ??
        row?.studyIdRef ??
        row?.StudyId ??
        row?.value ??
        "",
    ),
    label: String(row?.StudyName ?? row?.studyName ?? row?.label ?? "Study"),
    studyNumber: normalizePlannerStudyNumber(
      row?.StudyNumber ?? row?.studyNumber ?? row?.StudyNo ?? row?.studyNo ?? "",
    ),
    color: normalizePlannerStudyColor(
      row?.StudySchColor ??
        row?.studySchColor ??
        row?.StudyColor ??
        row?.studyColor ??
        row?.color ??
        "",
    ),
  }));
}

export function mapLocationOptions(rows = []) {
  return rows.map((row) => ({
    value: String(row?.LocationId ?? row?.locationId ?? row?.value ?? ""),
    label: String(row?.LocationName ?? row?.locationName ?? row?.label ?? "Location"),
  }));
}

function toIdSet(ids) {
  if (ids == null || ids === "") return null;
  const list = Array.isArray(ids) ? ids : [ids];
  const normalized = list.map((id) => String(id).trim()).filter(Boolean);
  return normalized.length ? new Set(normalized.map((id) => id.toUpperCase())) : null;
}

function buildNameLookup(options = [], valueKey = "value", labelKey = "label") {
  const map = {};
  options.forEach((option) => {
    const id = String(option?.[valueKey] ?? "").trim().toUpperCase();
    const label = String(option?.[labelKey] ?? "").trim();
    if (id && label) map[id] = label;
  });
  return map;
}

/** Filter groups by selected study id(s). Empty selection = all studies. */
export function mapGroupOptions(rows = [], studyIds = [], studyOptions = []) {
  const studySet = toIdSet(studyIds);
  const studyNameById = buildNameLookup(studyOptions);
  return rows
    .filter((row) => {
      if (!studySet) return true;
      const rowStudy = String(
        row?.StudyIdRef ??
          row?.studyIdRef ??
          row?.StudyScheduleId ??
          row?.studyScheduleId ??
          "",
      ).toUpperCase();
      return !rowStudy || studySet.has(rowStudy);
    })
    .map((row) => {
      const studyId = String(
        row?.StudyIdRef ??
          row?.studyIdRef ??
          row?.StudyScheduleId ??
          row?.studyScheduleId ??
          "",
      );
      const studyName =
        studyNameById[studyId.toUpperCase()] ||
        String(row?.StudyName ?? row?.studyName ?? "").trim();
      const groupName = String(row?.GroupName ?? row?.groupName ?? "Group");
      return {
        value: String(
          row?.GroupNameId ??
            row?.groupNameId ??
            row?.GroupNameIdRef ??
            row?.groupNameIdRef ??
            row?.GroupId ??
            row?.groupId ??
            "",
        ),
        label: groupName,
        description: studyName || "",
        studyId,
      };
    })
    .filter((option) => option.value);
}

/** Case-insensitive option lookup (GUIDs often differ only by case). */
export function findOptionByValue(options = [], value = "") {
  const key = String(value || "")
    .trim()
    .toUpperCase();
  if (!key) return null;
  return (
    (Array.isArray(options) ? options : []).find(
      (option) => String(option?.value || "").trim().toUpperCase() === key,
    ) || null
  );
}

/** Filter visits by selected group id(s). Empty selection = all groups. */
export function mapVisitOptions(rows = [], groupIds = [], context = {}) {
  const groupSet = toIdSet(groupIds);
  const studyOptions = context.studyOptions || [];
  const groupOptions = context.groupOptions || [];
  const studyNameById = buildNameLookup(studyOptions);
  const groupNameById = buildNameLookup(groupOptions);
  return rows
    .filter((row) => {
      if (!groupSet) return true;
      const rowGroup = String(
        row?.GroupNameIdRef ?? row?.groupNameIdRef ?? "",
      ).toUpperCase();
      return !rowGroup || groupSet.has(rowGroup);
    })
    .map((row) => {
      const studyId = String(row?.StudyIdRef ?? row?.studyIdRef ?? "");
      const groupId = String(row?.GroupNameIdRef ?? row?.groupNameIdRef ?? "");
      const studyName =
        studyNameById[studyId.toUpperCase()] ||
        String(row?.StudyName ?? row?.studyName ?? "").trim();
      const groupName =
        groupNameById[groupId.toUpperCase()] ||
        String(row?.GroupName ?? row?.groupName ?? "").trim();
      const visitName = String(row?.VisitName ?? row?.visitName ?? "Visit");
      const visitNo = Number(
        row?.VisitNo ?? row?.visitNo ?? row?.Visit ?? row?.visit,
      );
      const descriptionParts = [studyName, groupName].filter(Boolean);
      const slotDurationRaw =
        row?.SlotDuration ?? row?.slotDuration ?? row?.PeriodDuration ?? "";
      const slotDuration = Number(slotDurationRaw);
      return {
        value: String(row?.VisitsPerStudyId ?? row?.visitsPerStudyId ?? row?.VisitId ?? ""),
        label: visitName,
        description: descriptionParts.join(" · "),
        studyId,
        groupId,
        visitNo: Number.isFinite(visitNo) ? visitNo : null,
        isScreening:
          visitNo === 0 || /screening/i.test(visitName),
        slotDuration:
          Number.isFinite(slotDuration) && slotDuration > 0 ? slotDuration : 0,
        dayStartTime: String(
          row?.DayWindowStartTime ?? row?.dayWindowStartTime ?? "",
        ).trim(),
        dayEndTime: String(
          row?.DayWindowEndTime ?? row?.dayWindowEndTime ?? "",
        ).trim(),
      };
    })
    .filter((option) => option.value);
}

/** ISO yyyy-mm-dd → legacy SP custom date (DD/MM/YYYY). */
export function isoToEplannerMdy(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

/** ISO yyyy-mm-dd → US-style MM/DD/YYYY (legacy dynamic SQL CONVERT without style). */
export function isoToEplannerUsDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return "";
  return `${m}/${d}/${y}`;
}

/** Map shared DateRangeFilter value → E-Planner AppointmentPeriod API fields. */
export function mapEplannerDateRangeToApiPeriod(dateRange) {
  const preset = String(dateRange?.preset || dateRange?.dateRange || "").trim();
  const startIso = dateRange?.startDate || dateRange?.dateFrom || "";
  const endIso = dateRange?.endDate || dateRange?.dateTo || "";

  // All Time: no date filter (requires updated fast SP on local DB).
  if (preset === "All Time") {
    return {
      appointmentPeriod: "ALLTIME",
      startPeriod: "",
      endPeriod: "",
    };
  }

  const periodByPreset = {
    Today: "TODAY",
    Yesterday: "YESTERDAY",
    "This Week": "WEEK",
    "Previous Week": "PREVIOUS WEEK",
    "Last Week": "PREVIOUS WEEK",
    "This Month": "MONTH",
    "Previous Month": "PREVIOUS MONTH",
    "Last Month": "PREVIOUS MONTH",
    "This Quarter": "QUARTER",
    "Previous Quarter": "PREVIOUS QUARTER",
    "Last Quarter": "PREVIOUS QUARTER",
    "This Year": "YEAR",
    "Previous Year": "PREVIOUS YEAR",
    "Last Year": "PREVIOUS YEAR",
    Tomorrow: "TOMORROW",
    "Next Week": "NEXT WEEK",
    "Next Month": "NEXT MONTH",
    "Next Quarter": "NEXT QUARTER",
    "Next Year": "NEXT YEAR",
  };

  if (periodByPreset[preset]) {
    return {
      appointmentPeriod: periodByPreset[preset],
      startPeriod: "",
      endPeriod: "",
    };
  }

  if (startIso && endIso) {
    return {
      appointmentPeriod: "Custom",
      startPeriod: isoToEplannerMdy(startIso),
      endPeriod: isoToEplannerMdy(endIso),
    };
  }

  return {
    appointmentPeriod: "TODAY",
    startPeriod: "",
    endPeriod: "",
  };
}

/** True when every available option id is selected (treat as no filter). */
export function isAllOptionsSelected(selectedIds = [], optionIds = []) {
  if (!optionIds.length) return true;
  if (!selectedIds.length) return true;
  if (selectedIds.length < optionIds.length) return false;
  const selectedSet = new Set(selectedIds.map((id) => String(id).toUpperCase()));
  return optionIds.every((id) => selectedSet.has(String(id).toUpperCase()));
}

export function matchesEplannerIdFilter(row, field, selectedIds, optionIds) {
  if (!selectedIds?.length || isAllOptionsSelected(selectedIds, optionIds)) return true;
  const rowValue = String(getAppointmentRowValue(row, field) ?? "").toUpperCase();
  if (!rowValue) return true;
  return selectedIds.some((id) => String(id).toUpperCase() === rowValue);
}

function normalizeStatusFilterKey(status) {
  const text = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  if (text === "notattempt" || text.includes("didnotattend") || text.includes("didnot")) {
    return "notattempt";
  }
  return text;
}

export function matchesAppointmentStatusFilter(row, selectedStatuses = [], optionStatuses = []) {
  if (!selectedStatuses?.length || isAllOptionsSelected(selectedStatuses, optionStatuses)) {
    return true;
  }
  const rowStatus = normalizeStatusFilterKey(
    getAppointmentRowValue(row, "AppointmentStatus") ?? row?.AppointmentStatus,
  );
  return selectedStatuses.some(
    (status) => normalizeStatusFilterKey(status) === rowStatus,
  );
}

/** Client-side multi-select staff name filter for View Appointments column. */
export function matchesAppointmentStaffFilter(
  row,
  selectedNames = [],
  optionNames = [],
) {
  if (
    !selectedNames?.length ||
    isAllOptionsSelected(selectedNames, optionNames)
  ) {
    return true;
  }
  const names = parseAppointmentStaffNames(row).map((name) =>
    String(name || "")
      .trim()
      .toLowerCase(),
  );
  if (!names.length) return false;
  const selected = selectedNames
    .map((name) => String(name || "").trim().toLowerCase())
    .filter(Boolean);
  return selected.some((needle) =>
    names.some((name) => name === needle || name.includes(needle)),
  );
}

/** Omit LocationIds on fast API when all/none selected so NULL-location appointments are included. */
export function resolveApiLocationIds(selectedIds = [], optionIds = []) {
  if (!optionIds.length) return null;
  if (!selectedIds.length || isAllOptionsSelected(selectedIds, optionIds)) {
    return null;
  }
  return selectedIds;
}

/**
 * Legacy usp_EPlan_GetAppointmentInfo_NEWUI always applies a location IN clause.
 * Include '' plus all location ids so rows with NULL LocationId still match.
 */
export function resolveLegacyApiLocationIds(selectedIds = [], optionIds = []) {
  if (!optionIds.length) return null;
  if (!selectedIds.length || isAllOptionsSelected(selectedIds, optionIds)) {
    return ["", ...optionIds];
  }
  return selectedIds;
}

export function resolveSingleApiId(selectedIds = [], optionIds = []) {
  if (selectedIds.length === 1) return selectedIds[0];
  if (!selectedIds.length || isAllOptionsSelected(selectedIds, optionIds)) return null;
  return null;
}

export function formatEplannerApiDateTime(date) {
  const d = date instanceof Date ? date : new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

/** Month calendar range with legacy -7 / +13 day padding. */
export function buildCalendarMonthRange(monthDate) {
  const month = monthDate instanceof Date ? monthDate : new Date(monthDate);
  const start = new Date(month.getFullYear(), month.getMonth(), 1, 0, 0, 0);
  start.setDate(start.getDate() - 7);

  const end = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59);
  end.setDate(end.getDate() + 13);

  return {
    startDate: formatEplannerApiDateTime(start),
    endDate: formatEplannerApiDateTime(end),
  };
}

/** FullCalendar datesSet → API StartDate/EndDate (with padding). */
export function buildCalendarVisibleRange(start, end) {
  const rangeStart = start instanceof Date ? new Date(start) : new Date(start);
  const rangeEnd = end instanceof Date ? new Date(end) : new Date(end);
  if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) {
    return buildCalendarMonthRange(new Date());
  }
  rangeStart.setHours(0, 0, 0, 0);
  rangeStart.setDate(rangeStart.getDate() - 1);
  rangeEnd.setHours(23, 59, 59, 0);
  rangeEnd.setDate(rangeEnd.getDate() + 1);
  return {
    startDate: formatEplannerApiDateTime(rangeStart),
    endDate: formatEplannerApiDateTime(rangeEnd),
  };
}

/** Normalize calendar API rows so details panel can read list-style fields. */
export function normalizeCalendarAppointmentRow(row = {}) {
  if (!row || typeof row !== "object") return row;
  const start =
    row.StartTime ?? row.startTime ?? row.start ?? row.Start ?? "";
  const end = row.EndTime ?? row.endTime ?? row.end ?? row.End ?? start;
  const studyColor =
    String(
      row.StudySchColor ?? row.studySchColor ?? row.color ?? row.Color ?? "",
    ).trim() || "#3a87ad";

  let slotDuration = row.SlotDuration ?? row.slotDuration ?? "";
  if (!String(slotDuration || "").trim() && start && end) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (
      !Number.isNaN(startDate.getTime()) &&
      !Number.isNaN(endDate.getTime()) &&
      endDate > startDate
    ) {
      slotDuration = String(
        Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / 60000)),
      );
    }
  }

  const volNum = String(row.VolNumId ?? row.volNumId ?? "").trim();
  const existingVolId = String(row.VolId ?? row.volId ?? "").trim();
  const volId =
    existingVolId ||
    (volNum && !/^[0-9a-f-]{36}$/i.test(volNum) ? volNum : "");
  const statusText = String(row.AppointmentStatus ?? "");
  const checkResch =
    row.CheckResch ??
    (/confirmed|notattempt|did not attend/i.test(statusText) ? "ReSch" : "");

  const allDay = isAllDayAppointment(row);
  const label = isCalendarLabel(row);
  const labelBg = label ? resolveCalendarLabelBackground(row) : studyColor;
  const labelText = label ? resolveCalendarLabelTextColor(row) : "";
  return {
    ...row,
    StartTime: row.StartTime || start,
    EndTime: row.EndTime || end,
    StudySchColor: labelBg,
    color: labelBg,
    Color: label ? labelBg : row.Color ?? row.color,
    TextColor: label ? labelText : row.TextColor ?? row.textColor,
    allDay,
    AllDay: allDay ? 1 : 0,
    IsCalendarLabel: label ? 1 : row.IsCalendarLabel ?? 0,
    LabelComment: resolveCalendarLabelComment(row),
    Comment: resolveCalendarLabelComment(row) || row.Comment || row.comment || null,
    SlotDuration: slotDuration || row.SlotDuration || "",
    VolId: volId || row.VolId,
    RdId: row.RdId ?? row.rdId ?? row.RandomId ?? "",
    CheckResch: checkResch,
  };
}

/** Same rules as list inline reschedule control. */
export function canRescheduleAppointment(appointment, canManage = false) {
  if (!canManage || !appointment) return false;
  if (isCalendarLabel(appointment) || isAllDayAppointment(appointment)) {
    return false;
  }
  const status = String(
    getAppointmentRowValue(appointment, "AppointmentStatus") ??
      appointment?.AppointmentStatus ??
      "",
  );
  const statusValue = /notattempt|did not attend/i.test(status)
    ? "NotAttempt"
    : status || "Confirmed";
  if (statusValue === "Arrived") return false;
  if (String(appointment?.InActive ?? "0") === "1") return false;
  return (
    String(appointment?.CheckResch ?? "") === "ReSch" ||
    /confirmed|notattempt/i.test(status)
  );
}

/** Map StudyWiseView / VisitWiseInfo rows to FullCalendar events. */
export function mapAppointmentsToCalendarEvents(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => {
      const appointment = normalizeCalendarAppointmentRow(row);
      const id = String(
        appointment?.AppointmentId ??
          appointment?.appointmentId ??
          appointment?.id ??
          `evt-${index}`,
      ).trim();
      const start =
        appointment?.start ??
        appointment?.StartTime ??
        appointment?.Start ??
        appointment?.startTime;
      const end =
        appointment?.end ??
        appointment?.EndTime ??
        appointment?.End ??
        appointment?.endTime ??
        start;
      if (!id || !start) return null;
      const allDay = isAllDayAppointment(appointment);
      const isLabel = isCalendarLabel(appointment);
      const labelName = String(
        appointment?.title ||
          appointment?.Title ||
          appointment?.VolName ||
          appointment?.volName ||
          "",
      ).trim();
      const composedTitle = [
        appointment?.VolName ?? appointment?.volName,
        appointment?.VisitName ?? appointment?.visitName,
        appointment?.StudyName ?? appointment?.studyName,
      ]
        .filter(Boolean)
        .join(" · ");
      const title = isLabel
        ? labelName || "Label"
        : String(
            appointment?.title ||
              appointment?.Title ||
              composedTitle ||
              "Appointment",
          );
      const labelBg = isLabel
        ? resolveCalendarLabelBackground(appointment)
        : null;
      const labelText = isLabel
        ? resolveCalendarLabelTextColor(appointment)
        : null;
      const studyColor = isLabel
        ? labelBg
        : String(appointment?.StudySchColor || "#3a87ad").trim() || "#3a87ad";
      return {
        id,
        title,
        start,
        end: end || start,
        allDay: isLabel ? allDay : allDay,
        backgroundColor: isLabel ? labelBg : "#f1f5f9",
        borderColor: isLabel ? labelBg : "#e2e8f0",
        textColor: isLabel ? labelText : "#0f172a",
        extendedProps: { appointment, studyColor, isLabel },
      };
    })
    .filter(Boolean);
}
